'use client';

import {Currency, currencySymbolByCode, money} from '@rocket-ui/tools';
import React, {useCallback, useLayoutEffect, useState} from 'react';
import {Assign} from 'utility-types';
import {Input} from './Input';
import {InputProps} from './types';

export type MoneyInputValue = number | undefined;
export type MoneyInputOnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, value: MoneyInputValue) => void;

export type MoneyInputProps = Assign<
	Omit<InputProps, 'placeholder' | 'prefix' | 'suffix' | 'dynamicPlaceholder'>,
	{
		value: MoneyInputValue;
		currency?: keyof typeof currencySymbolByCode;
		onChange?: MoneyInputOnChangeHandler;
	}
>;

const formatValue = (value: MoneyInputValue, currency?: keyof typeof currencySymbolByCode): string =>
	value !== undefined ? money.format(value, {symbolCode: currency ?? Currency.RUB}).clearedValue : '';

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
	({value: inputValue, currency, onChange, ...props}, ref) => {
		const [value, setValue] = useState(formatValue(inputValue, currency));

		useLayoutEffect(() => {
			setValue(formatValue(inputValue, currency));
		}, [inputValue, setValue]);

		const handleChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const newValue = event.target.value.replace(',', '.');
				const numericNewValue = Number(newValue.replace(/\s/g, ''));

				if (!Number.isFinite(numericNewValue)) {
					return event.preventDefault();
				}

				// Если заканчиваемя на точку или точку-нолик, то меняем значение только локально,
				// т.к. если сделать onChange, то точка/точка-нолик там схлопнится
				if (newValue.endsWith('.') || newValue.endsWith('.0')) {
					return setValue(newValue);
				}

				// Если старое значение заканчивается на точку, а новое уже нет,
				// то делаем этот доп. апдейт локального значения, чтобы затереть точку
				if (value.endsWith('.')) {
					setValue(formatValue(numericNewValue, currency));
				}

				onChange?.(event, numericNewValue);
			},
			[onChange, setValue, value],
		);

		return (
			<Input
				ref={ref}
				value={value}
				suffix={money.currencySymbolByCode[currency ?? 'RUB']}
				dynamicPlaceholder="0"
				onChange={handleChange}
				{...props}
			/>
		);
	},
);
